import StatCardGrid from "@/components/StatCardGrid";
import TenantLicenseTable from "@/components/UserTable/UserTable";
import { Box, CircularProgress, Autocomplete, TextField } from "@mui/material";
import { FaUser, FaFileAlt, FaUsers, FaListAlt } from "react-icons/fa";
import { PiFilmSlateFill } from "react-icons/pi";
import { MdOutlineVideoCameraFront } from "react-icons/md";
import { useTenantDashboard, useTenantUsageGraph } from "@/hooks/useTenantDashboardService";
import TokenUsageChart from "@/components/TokenUsageChart/TokenUsageChart";
import { useState } from "react";
import { useUserStore } from "@/store";
import { useNavigate } from "react-router-dom";
import type { UserData } from "@/types/user";
import { useDashboardTheme } from "../MeetingDashboard/themeHelper";

const TenantDashboard = () => {
  const { colors } = useDashboardTheme();
  const { data: tenantDashboard, isLoading, isError } = useTenantDashboard();
  const [selectedUserId, setSelectedUserId] = useState<string>("ALL");

  const [chartType, setChartType] = useState<"yearly" | "monthly">("yearly");
  const [chartMonth, setChartMonth] = useState<number>(new Date().getMonth() + 1);
  const tenantId = localStorage.getItem("tenantId") || "";

  const trackerParams = chartType === "yearly" 
    ? { type: "yearly" as const, year: new Date().getFullYear() }
    : { type: "monthly" as const, year: new Date().getFullYear(), month: chartMonth };

  const { data: usageData } = useTenantUsageGraph(tenantId, {
    ...trackerParams,
    ...(selectedUserId !== "ALL" ? { userId: selectedUserId } : {})
  });


  const users = tenantDashboard?.data?.tenantUserList?.items ?? [];
  const licenseData = users.map((user: UserData) => ({
    name: user.name,
    email: user.email,
    isVerified: user.isVerified ?? false,
    status: user.status ?? false,
    role: user.role || "N/A",
    userImg: user.userImg || "",
    id: user.id || "",
  }));

  const stats = [
    {
      icon: <FaUsers className="text-black text-2xl" />,
      label: "Total Users",
      value: tenantDashboard?.data?.totalUserCount ?? 0,
    },
    {
      icon: <PiFilmSlateFill className="text-black text-xl" />,
      label: "Total Pitches",
      value: tenantDashboard?.data?.totalPitchCount ?? 0,
    },
    {
      icon: <FaFileAlt className="text-black text-xl" />,
      label: "Total Scripts",
      value: tenantDashboard?.data?.totalScriptCount ?? 0,
    },
    {
      icon: <MdOutlineVideoCameraFront className="text-black text-2xl" />,
      label: "Total Meetings",
      value: tenantDashboard?.data?.totalMeetingCount ?? 0,
    },
    {
      icon: <FaUser className="text-black text-xl" />,
      label: "Total Quick-Pitch",
      value: tenantDashboard?.data?.totalQuickPitchCount ?? 0,
    },
    {
      icon: <FaListAlt className="text-black text-xl" />,
      label: "Total Scenarios",
      value: tenantDashboard?.data?.totalScenarioCount ?? 0,
    },
  ];

  const scenarios = tenantDashboard?.data?.tenantScenarios ?? [];

  const { setEditUserData, setIsAddingUser } = useUserStore();
  const navigate = useNavigate();

  const handleEditUser = (user: UserData) => {
    setEditUserData(user);
    setIsAddingUser(true);
    navigate("/users");
  };

  if (isLoading)
    return (
      <div className="p-6">
        <CircularProgress color="secondary" />
      </div>
    );
  if (isError)
    return (
      <div className="p-6 text-red-500 font-semibold">
        Failed to load dashboard data. Please try again later.
      </div>
    );

  return (
    <main className="flex-1 md:p-6 mt-15 transition-all duration-300" style={{ background: colors.bg, color: colors.text }}>
      <StatCardGrid stats={stats} />


      <div className="mt-8 mb-4 flex items-center justify-between">
        <h2 className="text-xl font-bold" style={{ color: colors.text }}>Token Usage Overview</h2>
        <div className="flex gap-4 items-center">
          <select
            value={chartType}
            onChange={(e) => setChartType(e.target.value as "yearly" | "monthly")}
            className="p-2 border rounded-md"
            style={{ borderColor: colors.border, background: colors.surface, color: colors.text }}
          >
            <option value="yearly">Yearly View</option>
            <option value="monthly">Monthly View</option>
          </select>

          {chartType === "monthly" && (
            <select
              value={chartMonth}
              onChange={(e) => setChartMonth(Number(e.target.value))}
              className="p-2 border rounded-md"
              style={{ borderColor: colors.border, background: colors.surface, color: colors.text }}
            >
              {Array.from({ length: 12 }, (_, i) => (
                <option key={i + 1} value={i + 1}>
                  {new Date(0, i).toLocaleString('default', { month: 'long' })}
                </option>
              ))}
            </select>
          )}

            <Autocomplete
              options={users}
              getOptionLabel={(option: any) => `${option.name} (${option.email})`}
              value={users.find((u: any) => u.id === selectedUserId) || null}
              onChange={(_, newValue: any) => {
                setSelectedUserId(newValue ? newValue.id : "ALL");
              }}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  placeholder="Search User..." 
                  size="small"
                />
              )}
              sx={{
                width: 250,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '0.375rem',
                  color: colors.text,
                  '& fieldset': {
                    borderColor: colors.border,
                  },
                  '&:hover fieldset': {
                    borderColor: colors.border,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: colors.text,
                  },
                },
                '& .MuiSvgIcon-root': {
                  color: colors.text,
                },
                '& .MuiAutocomplete-inputRoot': {
                  padding: '1px !important',
                },
                '& .MuiAutocomplete-input': {
                  padding: '7px 4px !important',
                }
              }}
            />
        </div>
      </div>
      <TokenUsageChart data={usageData} title={selectedUserId === "ALL" ? "Company Token Usage" : "User Token Usage"} />

      {scenarios.length > 0 && (
        <section className="mt-8 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold" style={{ color: colors.text }}>Scenarios</h2>
            <button
              onClick={() => navigate('/practice-engine')}
              className="text-sm font-semibold hover:underline text-[#B8F890]"
            >
              View all Scenarios
            </button>
          </div>
          <div className="grid xs:grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {scenarios.slice(0, 6).map((scenario: any, index: number) => {
              const displayTitle = scenario.scenario?.title ?? scenario.title ?? scenario.scenarioTitle ?? `Scenario ${index + 1}`;
              const displayIndustry = scenario.scenario?.industry ?? scenario.industry;
              const displayMeetingType = scenario.scenario?.meetingType ?? scenario.meetingType ?? scenario.type ?? "Scenario";
              const sessionCount = scenario.totalSessionCount ?? scenario.sessionCount ?? "-";
              return (
                <div
                  key={scenario.scenarioId ?? scenario.id ?? scenario._id ?? index}
                  onClick={() => navigate('/practice-engine', { state: { search: displayTitle } })}
                  className="rounded-3xl p-5 flex flex-col justify-between shadow-sm transition-all duration-300 cursor-pointer"
                  style={{
                    background: colors.surface,
                    border: `1px solid ${colors.border}`,
                    color: colors.text,
                  }}
                >
                  <div>
                    <div className="text-lg font-semibold mb-2" style={{ color: colors.text }}>
                      {displayTitle}
                    </div>
                    {displayIndustry && (
                      <div className="text-sm mb-1" style={{ color: colors.textMuted }}>{displayIndustry}</div>
                    )}
                    <div className="text-sm mb-3 capitalize" style={{ color: colors.textMuted }}>
                      {displayMeetingType}
                    </div>
                  </div>
                  <div className="text-sm pt-2 flex items-center justify-between" style={{ borderTop: `1px solid ${colors.border}`, color: colors.textMuted }}>
                    <span>Total Sessions:</span>
                    <span className="font-semibold" style={{ color: colors.text }}>{sessionCount}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      <Box sx={{ overflowX: "auto", mt: 3 }}>
        <TenantLicenseTable
          data={licenseData}
          showHeading={true}
          onEditUser={handleEditUser}
        />
      </Box>
    </main>
  );
};
export default TenantDashboard;
